import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import COEHeader from './COEHeader';
import { useLocation } from 'react-router-dom';

// Elite Professional Skeleton Loading State
const AcademicContextLoading = () => {
  return (
    <div className="w-full bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden animate-pulse">
      <div className="bg-slate-50 border-b border-slate-100 px-6 py-5 flex items-center justify-between">
        <div className="space-y-2">
          <div className="h-5 w-48 bg-slate-200 rounded-md"></div>
          <div className="h-3.5 w-64 bg-slate-200 rounded-md"></div>
        </div>
        <div className="h-4 w-4 bg-slate-200 rounded-full"></div>
      </div>
      <div className="p-6 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="space-y-2">
              <div className="h-3 w-20 bg-slate-200 rounded"></div>
              <div className="h-10 bg-slate-100 rounded-xl border border-slate-200/60"></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Premium Responsive Academic Confirmation Modal
const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message, confirmText = "Confirm", cancelText = "Cancel", confirmVariant = "danger" }) => {
  if (!isOpen) return null;

  const getVariantStyles = () => {
    switch (confirmVariant) {
      case 'danger': return 'bg-rose-600 hover:bg-rose-700 focus:ring-rose-500 shadow-rose-100';
      case 'warning': return 'bg-amber-600 hover:bg-amber-700 focus:ring-amber-500 shadow-amber-100';
      case 'success': return 'bg-emerald-600 hover:bg-emerald-700 focus:ring-emerald-500 shadow-emerald-100';
      default: return 'bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500 shadow-indigo-100';
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
      <div className="flex items-end justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-slate-900/40 backdrop-blur-md" onClick={onClose}></div>
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
        
        <div className="inline-block w-full overflow-hidden text-left align-bottom transition-all transform bg-white rounded-2xl shadow-xl sm:my-8 sm:align-middle sm:max-w-lg border border-slate-100">
          <div className="p-6 bg-white">
            <div className="sm:flex sm:items-start">
              <div className="flex items-center justify-center flex-shrink-0 w-11 h-11 mx-auto bg-slate-50 border border-slate-200 rounded-xl sm:mx-0 sm:h-11 sm:w-11">
                <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="mt-4 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <h3 className="text-lg font-bold text-slate-900 tracking-tight">{title}</h3>
                <p className="mt-2 text-sm text-slate-500 leading-relaxed font-normal">{message}</p>
              </div>
            </div>
          </div>
          <div className="px-6 py-4 bg-slate-50/70 gap-3 border-t border-slate-100 flex flex-col-reverse sm:flex-row sm:justify-end">
            <button
              type="button"
              className="w-full sm:w-auto inline-flex justify-center px-4 py-2.5 text-sm font-semibold text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 hover:text-slate-900 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 shadow-sm"
              onClick={onClose}
            >
              {cancelText}
            </button>
            <button
              type="button"
              className={`w-full sm:w-auto inline-flex justify-center px-4 py-2.5 text-sm font-semibold text-white border border-transparent rounded-xl transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${getVariantStyles()}`}
              onClick={onConfirm}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper function to get faculty display name
const getFacultyDisplayName = (faculty) => {
  if (!faculty) return 'N/A';
  
  if (typeof faculty === 'string') return faculty;
  
  if (faculty.firstName || faculty.lastName) {
    const firstName = faculty.firstName || '';
    const lastName = faculty.lastName || '';
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || faculty.email || faculty.facultyId || 'N/A';
  }
  
  if (faculty.email) return faculty.email;
  if (faculty.facultyId) return faculty.facultyId;
  
  return 'N/A';
};

// Deeply Structured & Beautiful View Template Modal Component

// Accept context info as props for accurate display
const ViewTemplateModal = ({ isOpen, onClose, template, context }) => {
  if (!isOpen || !template) return null;

  const calculateTotalMarks = () => {
    return template.sections?.reduce((total, section) => total + (section.totalMarks || 0), 0) || 0;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'finalized': { bg: 'bg-emerald-50 border-emerald-200/70', text: 'text-emerald-700', label: 'Finalized' },
      'suspended': { bg: 'bg-amber-50 border-amber-200/70', text: 'text-amber-700', label: 'Suspended' },
      'draft': { bg: 'bg-slate-50 border-slate-200', text: 'text-slate-700', label: 'Draft' },
      'archived': { bg: 'bg-rose-50 border-rose-200/70', text: 'text-rose-700', label: 'Archived' }
    };
    const config = statusConfig[status] || statusConfig.finalized;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-semibold border rounded-full uppercase tracking-wider ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center overflow-y-auto">
      <div className="w-full flex items-center justify-center min-h-screen p-4 sm:p-6">
        <div className="fixed inset-0 transition-opacity bg-slate-900/40 backdrop-blur-md" onClick={onClose}></div>
        
        <div className="inline-block w-full max-w-4xl max-h-[85vh] my-4 overflow-hidden text-left align-middle transition-all transform bg-white rounded-2xl shadow-2xl border border-slate-100 flex flex-col">
          {/* Modal Header */}
          <div className="p-6 border-b border-slate-100 flex flex-col sm:flex-row sm:justify-between sm:items-start bg-slate-50/60 gap-4">
            <div className="space-y-1 flex-1 min-w-0">
              <div className="w-full overflow-x-auto">
                <div className="flex flex-wrap gap-4 items-center">
                  <div className="flex items-center gap-1 min-w-[120px]">
                    <span className="font-bold text-slate-700">Subject:</span>
                    <span className="text-base font-bold text-slate-900">{template.subjectName || context?.subjectName || '-'}</span>
                  </div>
                  <div className="flex items-center gap-1 min-w-[120px]">
                    <span className="font-bold text-slate-700">Subject Code:</span>
                    <span className="text-xs font-mono text-slate-700 bg-slate-100 border border-slate-300/40 px-2 py-0.5 rounded inline-block">{template.subjectCode || context?.subjectCode || '-'}</span>
                  </div>
                  {context?.programTypeName && (
                    <div className="flex items-center gap-1 min-w-[140px]">
                      <span className="font-bold text-blue-700">Program Type:</span>
                      <span className="text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded">{context.programTypeName}</span>
                    </div>
                  )}
                  {context?.programName && (
                    <div className="flex items-center gap-1 min-w-[120px]">
                      <span className="font-bold text-indigo-700">Program:</span>
                      <span className="text-xs font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 px-2 py-0.5 rounded">{context.programName}</span>
                    </div>
                  )}
                  {context?.academicYearName && (
                    <div className="flex items-center gap-1 min-w-[140px]">
                      <span className="font-bold text-emerald-700">Academic Year:</span>
                      <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded">{context.academicYearName}</span>
                    </div>
                  )}
                  {context?.semesterName && (
                    <div className="flex items-center gap-1 min-w-[120px]">
                      <span className="font-bold text-pink-700">Semester:</span>
                      <span className="text-xs font-semibold text-pink-700 bg-pink-50 border border-pink-200 px-2 py-0.5 rounded">{context.semesterName}</span>
                    </div>
                  )}
                  {context?.termName && (
                    <div className="flex items-center gap-1 min-w-[120px]">
                      <span className="font-bold text-cyan-700">Term:</span>
                      <span className="text-xs font-semibold text-cyan-700 bg-cyan-50 border border-cyan-200 px-2 py-0.5 rounded">{context.termName}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-xl hover:bg-slate-100 transition-colors">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Scrollable Contents */}
          <div className="p-6 overflow-y-auto flex-1 space-y-6">
            {/* Meta Details Dashboard Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-slate-50/60 border border-slate-200/60 rounded-xl text-sm shadow-inner">
              <div className="space-y-0.5">
                <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">Status</span>
                <div className="pt-0.5">{getStatusBadge(template.status)}</div>
              </div>
              <div className="space-y-0.5">
                <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">Total Blueprint Marks</span>
                <p className="font-extrabold text-slate-900 text-base">{calculateTotalMarks()} M</p>
              </div>
              <div className="space-y-0.5">
                <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">Finalized At</span>
                <p className="text-slate-700 font-medium">
                  {template.finalizedAt ? new Date(template.finalizedAt).toLocaleDateString() : '-'}
                </p>
              </div>
              <div className="space-y-0.5">
                <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">Configured By</span>
                <p className="font-semibold text-slate-900 truncate" title={getFacultyDisplayName(template.facultyId)}>
                  {getFacultyDisplayName(template.facultyId)}
                </p>
              </div>
            </div>

            {/* Structure Mapping Layout */}
            <div className="space-y-4">
              <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100 pb-2">Academic Assessment Blueprint</h4>
              {template.sections?.map((section, sectionIndex) => (
                <div key={section._id || sectionIndex} className="border border-slate-200/80 rounded-xl overflow-hidden shadow-sm">
                  <div className="bg-slate-50/80 px-4 py-3 border-b border-slate-200/80 flex justify-between items-center">
                    <h5 className="text-sm font-bold text-slate-800 tracking-tight">{section.sectionName}</h5>
                    <span className="text-xs font-mono font-bold bg-indigo-50 text-indigo-700 px-2.5 py-0.5 rounded-md border border-indigo-100">Weight: {section.sectionMaxMarks || section.totalMarks}M</span>
                  </div>
                  
                  <div className="p-4 divide-y divide-slate-100 space-y-4">
                    {section.questions?.map((question, qIndex) => (
                      <div key={question._id || qIndex} className="pt-4 first:pt-0 space-y-2">
                        <div className="flex justify-between items-start gap-4">
                          <div className="space-y-1.5 flex-1">
                            <span className="text-xs font-bold text-slate-400 uppercase tracking-wide block">Question {question.questionNumber}</span>
                            <p className="text-sm text-slate-800 font-medium leading-relaxed">{question.questionText}</p>
                            
                            {/* Subquestions Logic Nested Map */}
                            {question.hasSubQuestions && question.subQuestions?.length > 0 && (
                              <div className="mt-3 pl-4 border-l-2 border-slate-200 space-y-3 pt-1">
                                {question.subQuestions.map((subQ, subIndex) => (
                                  <div key={subQ._id || subIndex} className="text-sm text-slate-700 space-y-1">
                                    <p className="font-normal"><span className="font-bold text-slate-500">{subQ.subQuestionNumber})</span> {question.questionText} <span className="text-slate-400 font-mono text-xs">[{subQ.marks}M]</span></p>
                                    {(subQ.coMappings?.length > 0 || subQ.poMappings?.length > 0) && (
                                      <div className="flex flex-wrap gap-2 pt-0.5">
                                        {subQ.coMappings?.length > 0 && (
                                          <span className="bg-slate-100 text-slate-600 font-mono text-[10px] px-1.5 py-0.5 rounded">CO: {subQ.coMappings.map(c => c.coNumber).join(', ')}</span>
                                        )}
                                        {subQ.poMappings?.length > 0 && (
                                          <span className="bg-slate-100 text-slate-600 font-mono text-[10px] px-1.5 py-0.5 rounded">PO: {subQ.poMappings.map(p => p.poNumber).join(', ')}</span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {/* Baseline Question Mapping Indicators */}
                            {(question.coMappings?.length > 0 || question.poMappings?.length > 0) && !question.hasSubQuestions && (
                              <div className="flex flex-wrap gap-2 pt-1">
                                {question.coMappings?.length > 0 && (
                                  <span className="bg-slate-100 text-slate-500 border border-slate-200/50 font-mono text-[10px] px-2 py-0.5 rounded-md">CO: {question.coMappings.map(c => c.coNumber).join(', ')}</span>
                                )}
                                {question.poMappings?.length > 0 && (
                                  <span className="bg-slate-100 text-slate-500 border border-slate-200/50 font-mono text-[10px] px-2 py-0.5 rounded-md">PO: {question.poMappings.map(p => p.poNumber).join(', ')}</span>
                                )}
                              </div>
                            )}
                            
                            {question.bloomLevel && (
                              <div className="text-[10px] font-bold text-indigo-600 bg-indigo-50/50 border border-indigo-100 inline-block px-2 py-0.5 rounded-md tracking-wide uppercase">
                                Cognitive/Blooms Taxonomy Level: {question.bloomLevel}
                              </div>
                            )}
                          </div>
                          
                          <span className="text-xs font-bold font-mono bg-slate-50 text-slate-600 px-2.5 py-1 rounded-md border border-slate-200">
                            {question.marks}M
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Performance Ledger Summary */}
            {template.studentMarks?.length > 0 && (
              <div className="space-y-3">
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100 pb-2">Student Performance Matrix</h4>
                <div className="border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                  <table className="min-w-full divide-y divide-slate-100">
                    <thead className="bg-slate-50/80">
                      <tr>
                        <th className="px-5 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Student Ident</th>
                        <th className="px-5 py-3 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">Marks Obtained</th>
                        <th className="px-5 py-3 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">Percentage</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-sm text-slate-700 bg-white">
                      {template.studentMarks.map((mark, idx) => (
                        <tr key={idx} className="hover:bg-slate-50/50 transition-colors">
                          <td className="px-5 py-3 font-semibold text-slate-800">{mark.studentName || mark.studentId}</td>
                          <td className="px-5 py-3 text-right font-mono font-medium text-slate-900">{mark.marksObtained}</td>
                          <td className="px-5 py-3 text-right font-mono font-bold text-indigo-600">
                            {((mark.marksObtained / calculateTotalMarks()) * 100).toFixed(2)}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Modal Actions */}
          <div className="p-4 bg-slate-50 border-t border-slate-100 flex justify-end">
            <button type="button" className="px-4 py-2 text-sm font-semibold text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 hover:text-slate-900 shadow-sm transition-colors" onClick={onClose}>
              Dismiss View
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Finalized Template Management Component
export default function FinalizedTemplateManagement(props) {
  const location = useLocation();
  // Fallback logic for orgInfo and tenant
  const orgInfo = props.orgInfo || location.state?.orgInfo || (typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('orgInfo') || '{}') : {});
  const tenant = props.tenant || location.state?.tenant || (typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('tenant') || '{}') : {});
  // Dropdown options matrices
  const [programTypes, setProgramTypes] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [terms, setTerms] = useState([]);
  
  // Controlled selections state tracking
  const [selectedProgramType, setSelectedProgramType] = useState("");
  const [selectedProgram, setSelectedProgram] = useState("");
  const [selectedAcademicYear, setSelectedAcademicYear] = useState("");
  const [selectedSemester, setSelectedSemester] = useState("");
  const [selectedTerm, setSelectedTerm] = useState("");
  
  // Context management flags
  const [isSemesterBased, setIsSemesterBased] = useState(null);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(true);
  
  // Asynchronous status indicators
  const [loadingProgramTypes, setLoadingProgramTypes] = useState(false);
  const [loadingPrograms, setLoadingPrograms] = useState(false);
  const [loadingAcademicYears, setLoadingAcademicYears] = useState(false);
  const [loadingSemestersTerms, setLoadingSemestersTerms] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  
  // Core template array payload
  const [templates, setTemplates] = useState([]);
  
  // Dialog configuration management
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    confirmText: 'Confirm',
    confirmVariant: 'danger'
  });
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Tenant calculation parameters
  const tenantId = tenant?.subdomain || tenant?.id || tenant?._id || localStorage.getItem('tenantId');

  // Academic validation logic rules
  const isAcademicYearCompleted = useCallback((academicYear) => {
    return academicYear?.status === 'completed' || 
           academicYear?.isCompleted === true ||
           academicYear?.academicYearStatus === 'completed';
  }, []);

  const isSemesterCompleted = useCallback((semester) => {
    return semester?.status === 'completed' || 
           semester?.isCompleted === true ||
           semester?.semesterStatus === 'completed';
  }, []);

  const isTermCompleted = useCallback((term) => {
    return term?.status === 'completed' || 
           term?.isCompleted === true ||
           term?.termStatus === 'completed';
  }, []);

  // Sync program categories
  useEffect(() => {
    if (!tenantId) return;
    setLoadingProgramTypes(true);
    api.get('/api/program-types', { params: { tenantId, status: 'active' } })
      .then(res => {
        const types = res.data.programTypes || [];
        setProgramTypes(types);
        setError('');
      })
      .catch(() => setError('Failed to load program types'))
      .finally(() => setLoadingProgramTypes(false));
  }, [tenantId]);

  // Handle active subcategories selection lookup
  useEffect(() => {
    if (!tenantId || !selectedProgramType) {
      setPrograms([]);
      return;
    }
    setLoadingPrograms(true);
    api.get('/api/programs', { params: { tenantId, programTypeId: selectedProgramType, status: 'active' } })
      .then(res => {
        const progs = res.data.programs || [];
        setPrograms(progs);
        setError('');
      })
      .catch(() => setError('Failed to load programs'))
      .finally(() => setLoadingPrograms(false));
  }, [tenantId, selectedProgramType]);

  // Sync general system operational years
  useEffect(() => {
    if (!tenantId) return;
    setLoadingAcademicYears(true);
    Promise.all([
      api.get('/api/academic-year/all-active', { params: { tenantId } }),
      api.get('/api/academic-year/all-completed', { params: { tenantId } })
    ])
      .then(([activeRes, completedRes]) => {
        const activeYears = activeRes.data.academicYears || activeRes.data.activeAcademicYears || [];
        const completedYears = completedRes.data.academicYears || [];
        const years = [...activeYears, ...completedYears];
        setAcademicYears(years);
        setError('');
      })
      .catch(() => setError('Failed to load academic years'))
      .finally(() => setLoadingAcademicYears(false));
  }, [tenantId]);

  // Process term variations for chosen targets
  useEffect(() => {
    if (!selectedAcademicYear) {
      setSemesters([]);
      setTerms([]);
      setIsSemesterBased(null);
      return;
    }

    const selectedAY = academicYears.find(y => y._id === selectedAcademicYear);
    if (selectedAY && isAcademicYearCompleted(selectedAY)) {
      setError('This academic year is completed. View only mode.');
    } else {
      setError('');
    }

    setLoadingSemestersTerms(true);
    setSemesters([]);
    setTerms([]);
    setIsSemesterBased(null);
    
    api.get(`/api/classManager/semesters/${selectedAcademicYear}`)
      .then(res => {
        const sems = res.data.semesters || [];
        if (sems.length > 0) {
          setSemesters(sems);
          setIsSemesterBased(true);
        } else {
          api.get(`/api/academic-year/${selectedAcademicYear}/terms`, { params: { tenantId } })
            .then(res2 => {
              const trms = res2.data.terms || [];
              setTerms(trms);
              setIsSemesterBased(false);
            })
            .catch(() => setError('Failed to load terms'));
        }
      })
      .catch(() => {
        api.get(`/api/academic-year/${selectedAcademicYear}/terms`, { params: { tenantId } })
          .then(res2 => {
            const trms = res2.data.terms || [];
            setTerms(trms);
            setIsSemesterBased(false);
          })
          .catch(() => setError('Failed to load semesters/terms'));
      })
      .finally(() => setLoadingSemestersTerms(false));
  }, [selectedAcademicYear, tenantId, academicYears, isAcademicYearCompleted]);


  // Read data blueprint templates via standard API call (memoized)
  const fetchTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    setError('');
    try {
      const params = {
        tenantId,
        academicYearId: selectedAcademicYear,
        programTypeId: selectedProgramType,
        programId: selectedProgram
      };
      if (isSemesterBased === true && selectedSemester) {
        params.semId = selectedSemester;
      } else if (isSemesterBased === false && selectedTerm) {
        params.termId = selectedTerm;
      }
      const response = await api.get('/api/finalized-templates', { params });
      const fetchedTemplates = response.data?.data || response.data?.templates || [];
      setTemplates(fetchedTemplates);
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError(err.response?.data?.message || 'Failed to fetch finalized templates');
      setTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  }, [tenantId, selectedAcademicYear, selectedProgramType, selectedProgram, isSemesterBased, selectedSemester, selectedTerm]);

  // Sync templates selection updates
  useEffect(() => {
    if (
      selectedProgramType &&
      selectedProgram &&
      selectedAcademicYear &&
      ((isSemesterBased === true && selectedSemester) || (isSemesterBased === false && selectedTerm))
    ) {
      fetchTemplates();
    } else {
      setTemplates([]);
    }
  }, [selectedProgramType, selectedProgram, selectedAcademicYear, selectedSemester, selectedTerm, isSemesterBased, fetchTemplates]);

  // Restrict template accessibility status code
  const handleSuspendTemplate = async (templateId) => {
    setConfirmationModal({
      isOpen: true,
      title: 'Suspend Template Architecture',
      message: 'Are you sure you want to suspend this template? Suspended templates will not be available for ongoing configurations across active testing blocks.',
      confirmText: 'Yes, Suspend Hold',
      confirmVariant: 'warning',
      onConfirm: async () => {
        try {
          setLoadingTemplates(true);
          await api.patch(`/api/finalized-templates/${templateId}/suspend`, { reason: 'Suspended by COE' });
          await fetchTemplates();
          setError('');
          closeConfirmationModal();
        } catch (err) {
          console.error('Error suspending template:', err);
          setError(err.response?.data?.message || 'Failed to suspend template');
        } finally {
          setLoadingTemplates(false);
        }
      }
    });
  };

  // Restore operational availability access
  const handleActivateTemplate = async (templateId) => {
    setConfirmationModal({
      isOpen: true,
      title: 'Activate Framework Layout',
      message: 'Are you sure you want to activate this template? It will become dynamically deployed and available for use.',
      confirmText: 'Yes, Deploy Blueprint',
      confirmVariant: 'success',
      onConfirm: async () => {
        try {
          setLoadingTemplates(true);
          await api.patch(`/api/finalized-templates/${templateId}/activate`);
          await fetchTemplates();
          setError('');
          closeConfirmationModal();
        } catch (err) {
          console.error('Error activating template:', err);
          setError(err.response?.data?.message || 'Failed to activate template');
        } finally {
          setLoadingTemplates(false);
        }
      }
    });
  };

  // Erase targeted templates permanent structural record
  const handleDeleteTemplate = async (templateId) => {
    setConfirmationModal({
      isOpen: true,
      title: 'Irrevocable Delete Operation',
      message: 'Are you sure you want to permanently clear this template blueprint? This action is absolute and cannot be undone.',
      confirmText: 'Yes, Purge Record',
      confirmVariant: 'danger',
      onConfirm: async () => {
        try {
          setLoadingTemplates(true);
          await api.delete(`/api/finalized-templates/${templateId}`);
          await fetchTemplates();
          setError('');
          closeConfirmationModal();
        } catch (err) {
          console.error('Error deleting template:', err);
          setError(err.response?.data?.message || 'Failed to delete template');
        } finally {
          setLoadingTemplates(false);
        }
      }
    });
  };

  // Display detail target dialog mapper
  const handleViewTemplate = (template) => {
    setSelectedTemplate(template);
    setViewModalOpen(true);
  };

  const closeConfirmationModal = () => {
    setConfirmationModal({
      isOpen: false,
      title: '',
      message: '',
      onConfirm: null,
      confirmText: 'Confirm',
      confirmVariant: 'danger'
    });
  };

  // Intermediary contextual form state purgers
  // Only reset dependent dropdowns to minimize flicker
  const handleProgramTypeChange = (value) => {
    setSelectedProgramType(value);
    // Only reset downstream selections, not all
    if (selectedProgram) setSelectedProgram('');
    if (selectedAcademicYear) setSelectedAcademicYear('');
    if (selectedSemester) setSelectedSemester('');
    if (selectedTerm) setSelectedTerm('');
    setIsSemesterBased(null);
    setTemplates([]);
  };

  const handleProgramChange = (value) => {
    setSelectedProgram(value);
    setSelectedAcademicYear('');
    setSelectedSemester('');
    setSelectedTerm('');
    setIsSemesterBased(null);
    setTemplates([]);
  };

  const handleAcademicYearChange = (value) => {
    setSelectedAcademicYear(value);
    setSelectedSemester('');
    setSelectedTerm('');
    setIsSemesterBased(null);
    setTemplates([]);
  };

  const handleSemesterChange = (value) => {
    setSelectedSemester(value);
    setSelectedTerm('');
  };

  const handleTermChange = (value) => {
    setSelectedTerm(value);
    setSelectedSemester('');
  };

  // Internal visual formatting helpers
  const getAcademicYearLabel = (ayId) => {
    const ay = academicYears.find(y => y._id === ayId);
    if (!ay) return '';
    const isCompleted = isAcademicYearCompleted(ay);
    return `${ay.name || ay.academicYear}${isCompleted ? ' ✓ (Completed)' : ''}`;
  };

  const getSemesterLabel = (semId) => {
    const sem = semesters.find(s => s._id === semId);
    if (!sem) return '';
    const isCompleted = isSemesterCompleted(sem);
    return `${sem.name || sem.semesterLabel || `Semester ${sem.semesterNumber}`}${isCompleted ? ' ✓ (Completed)' : ''}`;
  };

  const getTermLabel = (tId) => {
    const term = terms.find(t => t._id === tId);
    if (!term) return '';
    const isCompleted = isTermCompleted(term);
    return `${term.name || term.termLabel || `Term ${term.termNumber}`}${isCompleted ? ' ✓ (Completed)' : ''}`;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'finalized': { bg: 'bg-emerald-50 border-emerald-200/70', text: 'text-emerald-700', label: 'Finalized' },
      'suspended': { bg: 'bg-amber-50 border-amber-200/70', text: 'text-amber-700', label: 'Suspended' },
      'draft': { bg: 'bg-slate-50 border-slate-200', text: 'text-slate-700', label: 'Draft' },
      'archived': { bg: 'bg-rose-50 border-rose-200/70', text: 'text-rose-700', label: 'Archived' }
    };
    const config = statusConfig[status] || statusConfig.finalized;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-semibold border rounded-full uppercase tracking-wider ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const isAnyLoading = loadingProgramTypes || loadingPrograms || loadingAcademicYears || loadingSemestersTerms;

  if (isAnyLoading) {
    return (
      <>
        <COEHeader orgInfo={orgInfo} tenant={tenant} />
        <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
          <AcademicContextLoading />
        </div>
      </>
    );
  }

  return (
    <>
      {/* Render view modal above header for proper overlay */}
      <ViewTemplateModal
        isOpen={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        template={selectedTemplate}
        context={{
          subjectName: selectedTemplate?.subjectName,
          subjectCode: selectedTemplate?.subjectCode,
          programTypeName: programTypes.find(pt => pt._id === selectedProgramType)?.name,
          programName: programs.find(p => p._id === selectedProgram)?.name,
          academicYearName: academicYears.find(ay => ay._id === selectedAcademicYear)?.name || academicYears.find(ay => ay._id === selectedAcademicYear)?.academicYear,
          semesterName: semesters.find(s => s._id === selectedSemester)?.name || semesters.find(s => s._id === selectedSemester)?.semesterLabel,
          termName: terms.find(t => t._id === selectedTerm)?.name || terms.find(t => t._id === selectedTerm)?.termLabel,
        }}
      />
      <COEHeader orgInfo={orgInfo} tenant={tenant} />
      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8 space-y-6 antialiased text-slate-800">
      {/* Dynamic Header Section */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-slate-200/60 pb-5">
        <div className="space-y-1">
          <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight sm:text-3xl">Finalized Template Management</h1>
          <p className="text-sm font-normal text-slate-500 max-w-2xl">
            Review curriculum mapping definitions, suspend blueprint architecture, or purge locked system assessment layouts across specific academic terms.
          </p>
        </div>
      </div>
      
      {/* Search Configuration & Academic Filters Component Block */}
      <div className="w-full bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden transition-all duration-300 group">
        <div 
          className="bg-slate-50/80 px-5 py-4 sm:px-6 border-b border-slate-200/80 flex items-center justify-between cursor-pointer select-none hover:bg-slate-50 transition-colors"
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex items-center space-x-3">
            <div className="p-1.5 bg-white border border-slate-200 text-indigo-600 rounded-lg shadow-sm">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l9-5-9-5-9 5 9 5z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l6.16-3.422A12.083 12.083 0 0112 21.5c-4.418 0-8-1.79-8-4V9" />
              </svg>
            </div>
            <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Academic Scope Matrix Selector</h2>
          </div>
          <div className="text-slate-400 p-1 rounded-lg hover:bg-slate-200/60 hover:text-slate-600 transition-all">
            <svg className={`w-4 h-4 transform transition-transform duration-300 ${expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        {expanded && (
          <div className="p-5 sm:p-6 bg-white space-y-4">
            {error && (
              <div className="p-3.5 bg-rose-50/50 border border-rose-200 text-rose-800 text-xs sm:text-sm rounded-xl flex items-center gap-3 shadow-inner">
                <svg className="w-4 h-4 flex-shrink-0 text-rose-600" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <span className="font-semibold leading-relaxed">{error}</span>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              <div className="space-y-1.5">
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 block">
                  Program Type <span className="text-rose-500 font-bold">*</span>
                </label>
                <div className="relative">
                  <select
                    value={selectedProgramType}
                    onChange={(e) => handleProgramTypeChange(e.target.value)}
                    className="w-full text-sm border border-slate-300/80 bg-white rounded-xl p-2.5 shadow-sm transition-all focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 focus:outline-none appearance-none cursor-pointer text-slate-700 font-medium"
                  >
                    <option value="">Choose Type</option>
                    {programTypes.map(pt => (
                      <option key={pt._id} value={pt._id}>{pt.name}</option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-slate-400">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                  </div>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 block">
                  Target Program <span className="text-rose-500 font-bold">*</span>
                </label>
                <div className="relative">
                  <select
                    value={selectedProgram}
                    onChange={(e) => handleProgramChange(e.target.value)}
                    disabled={!selectedProgramType}
                    className="w-full text-sm border border-slate-300/80 bg-white rounded-xl p-2.5 shadow-sm transition-all focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 focus:outline-none appearance-none disabled:bg-slate-50 disabled:text-slate-400 disabled:border-slate-200 cursor-pointer text-slate-700 font-medium"
                  >
                    <option value="">Choose Program</option>
                    {programs.map(p => (
                      <option key={p._id} value={p._id}>{p.name}</option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-slate-400">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                  </div>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 block">
                  Academic Calendar Session <span className="text-rose-500 font-bold">*</span>
                </label>
                <div className="relative">
                  <select
                    value={selectedAcademicYear}
                    onChange={(e) => handleAcademicYearChange(e.target.value)}
                    disabled={!selectedProgram}
                    className="w-full text-sm border border-slate-300/80 bg-white rounded-xl p-2.5 shadow-sm transition-all focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 focus:outline-none appearance-none disabled:bg-slate-50 disabled:text-slate-400 disabled:border-slate-200 cursor-pointer text-slate-700 font-medium"
                  >
                    <option value="">Choose Session</option>
                    {academicYears.map(ay => (
                      <option key={ay._id} value={ay._id}>
                        {getAcademicYearLabel(ay._id)}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-slate-400">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                  </div>
                </div>
              </div>

              {isSemesterBased === true && (
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 block">
                    Semester Block <span className="text-rose-500 font-bold">*</span>
                  </label>
                  <div className="relative">
                    <select
                      value={selectedSemester}
                      onChange={(e) => handleSemesterChange(e.target.value)}
                      disabled={!selectedAcademicYear || semesters.length === 0}
                      className="w-full text-sm border border-slate-300/80 bg-white rounded-xl p-2.5 shadow-sm transition-all focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 focus:outline-none appearance-none disabled:bg-slate-50 disabled:text-slate-400 disabled:border-slate-200 cursor-pointer text-slate-700 font-medium"
                    >
                      <option value="">Choose Semester</option>
                      {semesters.map(sem => (
                        <option key={sem._id} value={sem._id}>
                          {getSemesterLabel(sem._id)}
                        </option>
                      ))}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-slate-400">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                    </div>
                  </div>
                </div>
              )}

              {isSemesterBased === false && (
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 block">
                    Term Interval <span className="text-rose-500 font-bold">*</span>
                  </label>
                  <div className="relative">
                    <select
                      value={selectedTerm}
                      onChange={(e) => handleTermChange(e.target.value)}
                      disabled={!selectedAcademicYear || terms.length === 0}
                      className="w-full text-sm border border-slate-300/80 bg-white rounded-xl p-2.5 shadow-sm transition-all focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 focus:outline-none appearance-none disabled:bg-slate-50 disabled:text-slate-400 disabled:border-slate-200 cursor-pointer text-slate-700 font-medium"
                    >
                      <option value="">Choose Term</option>
                      {terms.map(term => (
                        <option key={term._id} value={term._id}>
                          {getTermLabel(term._id)}
                        </option>
                      ))}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-slate-400">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Templates Found View Panel */}
      {(selectedProgramType && selectedProgram && selectedAcademicYear && ((isSemesterBased === true && selectedSemester) || (isSemesterBased === false && selectedTerm))) && (
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden transition-all duration-300">
          <div className="px-5 py-4 bg-slate-50/80 border-b border-slate-200/80 flex items-center justify-between">
            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Identified Assessment BLUEPRINTS</h3>
            <span className="text-xs font-mono font-bold bg-slate-200 text-slate-700 px-2 py-0.5 rounded-md border border-slate-300/60">Records: {templates.length}</span>
          </div>
          
          {loadingTemplates ? (
            <div className="text-center py-16 space-y-3">
              <div className="inline-block w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-xs text-slate-400 font-bold tracking-wide uppercase">Securing verified schemas...</p>
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-16 text-sm text-slate-400 font-semibold space-y-1">
              <p>No blueprint data maps configured for this exact academic combination.</p>
              <p className="text-xs font-normal text-slate-400/80">Double-check criteria settings above if this looks incorrect.</p>
            </div>
          ) : (
            <div>
              {/* Desktop/Tablet Layout Matrix (Standard Screen Mode) */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead className="bg-slate-50/40 border-b border-slate-200 text-xs font-bold text-slate-400 uppercase tracking-wider">
                    <tr>
                      <th className="p-4 pl-6">Subject Structure</th>
                      <th className="p-4">Subject Code</th>
                      <th className="p-4">Assigned Instructor</th>
                      <th className="p-4">Status State</th>
                      <th className="p-4">Finalized On</th>
                      <th className="p-4 pr-6 text-right">Operations Matrix</th>
                    </tr>
                  </thead>
                  <tbody className="text-sm text-slate-700 divide-y divide-slate-100 bg-white">
                    {templates.map((template) => (
                      <tr key={template._id} className="hover:bg-indigo-50/20 transition-all group">
                        <td className="p-4 pl-6 font-bold text-slate-900 group-hover:text-indigo-900 transition-colors">{template.subjectName}</td>
                        <td className="p-4 font-mono text-xs text-slate-500 font-medium">
                          <span className="bg-slate-50 px-2 py-0.5 border border-slate-200 rounded-md">{template.subjectCode}</span>
                        </td>
                        <td className="p-4 text-slate-600 font-semibold text-xs">
                          {getFacultyDisplayName(template.facultyId)}
                        </td>
                        <td className="p-4">
                          {getStatusBadge(template.status)}
                        </td>
                        <td className="p-4 font-mono text-xs font-medium text-slate-400">
                          {template.finalizedAt ? new Date(template.finalizedAt).toLocaleDateString() : '-'}
                        </td>
                        <td className="p-4 pr-6 text-right space-x-1.5 whitespace-nowrap">
                          <button 
                            onClick={() => handleViewTemplate(template)}
                            className="text-xs font-bold text-indigo-600 bg-indigo-50/50 hover:bg-indigo-100 border border-indigo-200/50 px-3 py-1.5 rounded-xl transition-all shadow-sm"
                          >
                            View
                          </button>
                          {template.status === 'finalized' && (
                            <button 
                              onClick={() => handleSuspendTemplate(template._id)}
                              className="text-xs font-bold text-amber-600 bg-amber-50/50 hover:bg-amber-100 border border-amber-200/50 px-3 py-1.5 rounded-xl transition-all shadow-sm"
                            >
                              Suspend
                            </button>
                          )}
                          {template.status === 'suspended' && (
                            <button 
                              onClick={() => handleActivateTemplate(template._id)}
                              className="text-xs font-bold text-emerald-600 bg-emerald-50/50 hover:bg-emerald-100 border border-emerald-200/50 px-3 py-1.5 rounded-xl transition-all shadow-sm"
                            >
                              Activate
                            </button>
                          )}
                          <button 
                            onClick={() => handleDeleteTemplate(template._id)}
                            className="text-xs font-bold text-rose-600 bg-rose-50/50 hover:bg-rose-100 border border-rose-200/50 px-3 py-1.5 rounded-xl transition-all shadow-sm"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile View Stack Cards (Handheld Screen Mode) */}
              <div className="block md:hidden divide-y divide-slate-100 bg-white">
                {templates.map((template) => (
                  <div key={template._id} className="p-4 space-y-4 hover:bg-slate-50/40 transition-colors">
                    <div className="flex justify-between items-start gap-4">
                      <div className="space-y-1">
                        <h4 className="font-extrabold text-slate-900 text-sm leading-snug">{template.subjectName}</h4>
                        <span className="text-[10px] font-mono text-slate-500 bg-slate-100 px-2 py-0.5 rounded border border-slate-200/60 font-semibold inline-block">{template.subjectCode}</span>
                      </div>
                      <div className="flex-shrink-0 pt-0.5">
                        {getStatusBadge(template.status)}
                      </div>
                    </div>

                    <div className="text-xs text-slate-600 space-y-1.5 bg-slate-50/60 p-3 rounded-xl border border-slate-200/40 font-medium">
                      <div className="flex justify-between"><span className="text-slate-400 font-bold uppercase tracking-wide text-[9px]">Faculty Account</span> <span className="truncate max-w-[180px] text-slate-800 font-semibold">{getFacultyDisplayName(template.facultyId)}</span></div>
                      <div className="flex justify-between"><span className="text-slate-400 font-bold uppercase tracking-wide text-[9px]">Finalized Date</span> <span className="font-mono text-slate-800">{template.finalizedAt ? new Date(template.finalizedAt).toLocaleDateString() : '-'}</span></div>
                    </div>

                    <div className="grid grid-cols-3 gap-2 pt-1">
                      <button 
                        onClick={() => handleViewTemplate(template)}
                        className="text-center text-xs font-bold border border-slate-200 bg-white rounded-xl py-2 text-slate-700 active:bg-slate-100 transition-all shadow-sm"
                      >
                        View
                      </button>
                      {template.status === 'finalized' && (
                        <button 
                          onClick={() => handleSuspendTemplate(template._id)}
                          className="text-center text-xs font-bold border border-amber-200/60 bg-amber-50/50 rounded-xl py-2 text-amber-700 active:bg-amber-100 transition-colors"
                        >
                          Suspend
                        </button>
                      )}
                      {template.status === 'suspended' && (
                        <button 
                          onClick={() => handleActivateTemplate(template._id)}
                          className="text-center text-xs font-bold border border-emerald-200/60 bg-emerald-50/50 rounded-xl py-2 text-emerald-700 active:bg-emerald-100 transition-colors"
                        >
                          Activate
                        </button>
                      )}
                      <button 
                        onClick={() => handleDeleteTemplate(template._id)}
                        className="text-center text-xs font-bold border border-rose-200/60 bg-rose-50/50 rounded-xl py-2 text-rose-700 active:bg-rose-100 transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Operational Modal Panels Portals */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={closeConfirmationModal}
        onConfirm={confirmationModal.onConfirm}
        title={confirmationModal.title}
        message={confirmationModal.message}
        confirmText={confirmationModal.confirmText}
        confirmVariant={confirmationModal.confirmVariant}
      />

      {/* ViewTemplateModal is now rendered above header for overlay */}
      </div>
    </>
  );
}